import { db } from './firebase';
import { auth } from './firebase';
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  DocumentSnapshot,
  QueryDocumentSnapshot,
  DocumentReference,
  QueryConstraint,
  runTransaction,
  writeBatch,
  Timestamp,
  serverTimestamp,
  increment,
  setDoc,
} from 'firebase/firestore';
import { 
  FragranceProduct, 
  FragrancePromotion, 
  FragranceCode, 
  FragranceSalesStats,
  CommissionRule
} from '@/types/fragrance';
import { uploadImage } from './firestore/utils';
import { getPartnerCommissionPercentages, updatePartnerStats } from './partnerService';
import { recordPartnerSale, recordPartnerCommission } from './fragranceFinanceService';
import { updatePartnerStockOnSale, updatePartnerStockOnReturn } from './partnerStockService';

// Collection references
const productsCollection = collection(db, 'fragrance_products');
const promotionsCollection = collection(db, 'fragrance_promotions');
const codesCollection = collection(db, 'fragrance_codes');
// Global stats collection removed as per requirement
const partnersCollection = collection(db, 'fragrance_partners');

// Global stats document reference removed as per requirement

// Product functions
export async function getFragranceProducts() {
  const snapshot = await getDocs(query(productsCollection, orderBy('name')));
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FragranceProduct));
}

export async function getFragranceProduct(id: string) {
  const docRef = doc(productsCollection, id);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() } as FragranceProduct;
  }
  return null;
}

export async function createFragranceProduct(product: Omit<FragranceProduct, 'id' | 'createdAt' | 'updatedAt'>) {
  const docRef = await addDoc(productsCollection, {
    ...product,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docRef.id;
}

export async function updateFragranceProduct(id: string, product: Partial<Omit<FragranceProduct, 'id' | 'createdAt' | 'updatedAt'>>) {
  const docRef = doc(productsCollection, id);
  await updateDoc(docRef, {
    ...product,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteFragranceProduct(id: string) {
  const docRef = doc(productsCollection, id);
  await deleteDoc(docRef);
}

// Promotion functions
export async function getActivePromotion() {
  const q = query(
    promotionsCollection,
    where('isActive', '==', true),
    limit(1)
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  
  const doc = snapshot.docs[0];
  return { id: doc.id, ...doc.data() } as FragrancePromotion;
}

export async function getPromotions() {
  const snapshot = await getDocs(query(promotionsCollection, orderBy('createdAt', 'desc')));
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FragrancePromotion));
}

export async function createPromotion(promotion: Omit<FragrancePromotion, 'id' | 'createdAt' | 'updatedAt'>) {
  // First deactivate all other promotions if this one is active
  if (promotion.isActive) {
    const activePromotions = await getDocs(
      query(promotionsCollection, where('isActive', '==', true))
    );
    
    const batch = writeBatch(db);
    activePromotions.forEach(doc => {
      batch.update(doc.ref, { isActive: false, updatedAt: serverTimestamp() });
    });
    await batch.commit();
  }
  
  // Then create the new promotion
  const docRef = await addDoc(promotionsCollection, {
    ...promotion,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  
  return docRef.id;
}

export async function updatePromotion(id: string, promotion: Partial<Omit<FragrancePromotion, 'id' | 'createdAt' | 'updatedAt'>>) {
  // If setting this promotion to active, deactivate all others
  if (promotion.isActive) {
    const activePromotions = await getDocs(
      query(promotionsCollection, where('isActive', '==', true))
    );
    
    const batch = writeBatch(db);
    activePromotions.forEach(doc => {
      // Skip the current promotion
      if (doc.id === id) return;
      batch.update(doc.ref, { isActive: false, updatedAt: serverTimestamp() });
    });
    await batch.commit();
  }
  
  // Update the promotion
  const docRef = doc(promotionsCollection, id);
  await updateDoc(docRef, {
    ...promotion,
    updatedAt: serverTimestamp(),
  });
}

// Fragrance code functions
export async function generateFragranceCodes(fragranceId: string, count: number, partnerId: string) {
  const batch = writeBatch(db);
  const codes: string[] = [];
  
  // Get the product to validate it exists
  const productDoc = await getDoc(doc(productsCollection, fragranceId));
  if (!productDoc.exists()) {
    throw new Error('Product not found');
  }
  
  // Get the partner information
  let partnerCode = 'TEL'; // Default partner code if no partner is found
  
  if (partnerId && partnerId !== 'default') {
    try {
      const partnerDoc = await getDoc(doc(partnersCollection, partnerId));
      if (partnerDoc.exists()) {
        const partnerData = partnerDoc.data();
        partnerCode = partnerData?.code || 'TEL';
      } else {
        throw new Error('Partner not found');
      }
    } catch (error) {
      console.error(`Error fetching partner ${partnerId}:`, error);
      throw new Error('Partner not found or invalid');
    }
  }
  
  for (let i = 0; i < count; i++) {
    // Generate a unique code with TEL prefix and partner code
    // Format: TEL-[partner code]-XXXX-XXXX
    const code = `TEL-${partnerCode}-${generateRandomString(4)}-${generateRandomString(4)}`;
    codes.push(code);
    
    const newCodeRef = doc(codesCollection);
    batch.set(newCodeRef, {
      code,
      fragranceId,
      partnerId, // Add partnerId to the code
      status: "available", // Set initial status to available
      statusText: "Available", // Add statusText for UI display
      displayStatus: "Available", // Special field for UI display
      isUsed: false,
      isAssigned: false, // Add isAssigned flag
      isRedeemed: false,
      soldAt: null,
      redeemedAt: null,
      assignedAt: null,
      assignedToOrderId: null,
      soldBy: null,
      soldLocation: null,
      customerId: null,
      customerName: null,
      customerPhone: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }
  
  await batch.commit();
  return codes;
}

// Define filter options interface for getFragranceCodes
export interface FragranceCodeFilters {
  fragranceId?: string;
  partnerId?: string;
  status?: string;
  isRedeemed?: boolean;
  isUsed?: boolean;
}

export async function getFragranceCodes(filters: FragranceCodeFilters | string = {}) {
  const constraints: QueryConstraint[] = [];
  
  // Handle legacy string parameter (fragranceId)
  if (typeof filters === 'string') {
    constraints.push(where('fragranceId', '==', filters));
  } else {
    // Apply filters from the options object
    if (filters.fragranceId) {
      constraints.push(where('fragranceId', '==', filters.fragranceId));
    }
    
    if (filters.partnerId) {
      constraints.push(where('partnerId', '==', filters.partnerId));
    }
    
    if (filters.status) {
      constraints.push(where('status', '==', filters.status));
    }
    
    if (filters.isRedeemed !== undefined) {
      constraints.push(where('isRedeemed', '==', filters.isRedeemed));
    }
    
    if (filters.isUsed !== undefined) {
      constraints.push(where('isUsed', '==', filters.isUsed));
    }
  }
  
  constraints.push(orderBy('code'));
  
  const q = query(codesCollection, ...constraints);
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FragranceCode));
}

// Add function to get codes for a specific partner
// Define the return type for getPartnerCodes
interface PartnerCodesResult {
  codes: FragranceCode[];
  partnerExists: boolean;
  error?: Error;
}

export async function getPartnerCodes(partnerCode: string): Promise<PartnerCodesResult> {
  if (!partnerCode) {
    console.error('Partner code is required to fetch partner codes');
    return { codes: [], partnerExists: false };
  }

  console.log(`Fetching codes for partner code: ${partnerCode}`);
  
  // First, find the partner document ID using the partner code
  try {
    const partnerQuery = query(
      collection(db, 'fragrance_partners'),
      where('code', '==', partnerCode)
    );
    
    const partnerSnapshot = await getDocs(partnerQuery);
    
    if (partnerSnapshot.empty) {
      // Instead of an error, just log a warning since this might be a normal case
      console.warn(`No partner found with code: ${partnerCode}`);
      return { codes: [], partnerExists: false };
    }
    
    // Get the partner document ID
    const partnerId = partnerSnapshot.docs[0].id;
    console.log(`Found partner ID: ${partnerId} for code: ${partnerCode}`);
    
    // Now query for codes using the partner document ID
    const codesQuery = query(
      codesCollection,
      where('partnerId', '==', partnerId),
      orderBy('createdAt', 'desc')
    );
    
    const codesSnapshot = await getDocs(codesQuery);
    console.log(`Found ${codesSnapshot.size} codes for partner ID: ${partnerId}`);
    
    // Get the basic codes data
    const codes = codesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FragranceCode));
    
    // Fetch product information for each code
    const productsCollection = collection(db, 'fragrance_products');
    const productIds = [...new Set(codes.map(code => code.fragranceId))];
    
    // Create a map of product IDs to product names
    const productMap: Record<string, string> = {};
    
    // Only fetch products if there are codes
    if (productIds.length > 0) {
      // Due to Firestore limitations, we can't use 'in' with more than 10 values
      // So we'll split into chunks if needed
      const chunkSize = 10;
      const productIdChunks = [];
      
      for (let i = 0; i < productIds.length; i += chunkSize) {
        productIdChunks.push(productIds.slice(i, i + chunkSize));
      }
      
      // Fetch products for each chunk
      for (const chunk of productIdChunks) {
        const productsQuery = query(
          productsCollection,
          where('__name__', 'in', chunk)
        );
        
        const productsSnapshot = await getDocs(productsQuery);
        
        productsSnapshot.forEach(doc => {
          const product = doc.data() as FragranceProduct;
          productMap[doc.id] = product.name;
        });
      }
    }
    
    // Add product names to the codes
    const codesWithProducts = codes.map(code => ({
      ...code,
      productName: productMap[code.fragranceId] || 'Unknown Product'
    }));
    
    return { codes: codesWithProducts, partnerExists: true };
  } catch (error) {
    console.error('Error fetching partner codes:', error);
    return { codes: [], partnerExists: false, error: error as Error };
  }
}

export async function getUnusedCodes(fragranceId?: string) {
  // Try to use status field first, but also check isUsed and isAssigned for backward compatibility
  let q;
  
  // For Firestore queries with multiple conditions on different fields, we need to create a composite index
  // Here we're using a simpler approach with just the status field
  q = query(
    codesCollection,
    where('status', '==', 'available'),
    orderBy('createdAt')
  );
  
  if (fragranceId) {
    q = query(
      codesCollection,
      where('fragranceId', '==', fragranceId),
      where('status', '==', 'available'),
      orderBy('createdAt')
    );
  }
  
  const snapshot = await getDocs(q);
  
  // Filter results to ensure we only get truly available codes
  // This handles cases where status might be inconsistent with the flags
  const codes = snapshot.docs.map(doc => {
    const data = doc.data() as Omit<FragranceCode, 'id'>;
    return { id: doc.id, ...data };
  });
  return codes.filter(code => 
    !code.isUsed && !code.isAssigned && !code.isRedeemed && !code.isExpired
  );
}

// Add a new function for automatically checking and fixing inconsistencies after operations
export async function ensureStatsConsistency(partnerId: string): Promise<void> {
  try {
    if (!partnerId || partnerId === 'default') return;
    
    console.log(`Automatically checking stats consistency for partner: ${partnerId}`);
    
    // Import the detection and recalculation functions
    const { detectPartnerStatsIssues, recalculatePartnerStats } = await import('./partnerService');
    
    // Check for inconsistencies
    const issues = await detectPartnerStatsIssues(partnerId);
    
    // If inconsistencies are found, automatically fix them
    if (issues.hasIssues) {
      console.log(`Found ${issues.issues.length} issues with partner stats, auto-fixing:`, issues.issues);
      await recalculatePartnerStats(partnerId);
      console.log(`Auto-fixed partner stats for: ${partnerId}`);
    }
  } catch (error) {
    console.error('Error in automatic stats consistency check:', error);
    // Don't throw - we don't want to fail the main operation
  }
}

// Modify markCodeAsUsed to use the auto-consistency function
export async function markCodeAsUsed(
  codeId: string, 
  data: {
    soldBy: string;
    saleSource: string; // Dynamic partner code
    givePartnerCommission: boolean; // Whether to give commission to partner
    customerId?: string;
    customerName?: string;
    customerPhone?: string;
    isAdminInitiated?: boolean; // Flag to indicate if the sale was initiated by admin
  }
) {
  const codeRef = doc(codesCollection, codeId);
  
  // Run as a transaction to ensure data consistency
  return runTransaction(db, async (transaction) => {
    console.log(`Beginning transaction to mark code ${codeId} as used`);
    const codeDoc = await transaction.get(codeRef);
    
    if (!codeDoc.exists()) {
      throw new Error('Code not found');
    }
    
    const codeData = codeDoc.data() as FragranceCode;
    console.log(`Processing code: ${codeData.code}`);
    
    if (codeData.isUsed) {
      throw new Error('Code has already been used');
    }
    
    if (codeData.isRedeemed) {
      throw new Error('Code has already been redeemed');
    }
    
    // Get the product information for price
    const productRef = doc(productsCollection, codeData.fragranceId);
    const productDoc = await transaction.get(productRef);
    
    if (!productDoc.exists()) {
      throw new Error('Product not found');
    }
    
    const productData = productDoc.data() as FragranceProduct;
    const price = productData.price || 0;
    console.log(`Product price: ${price}`);
    
    // Calculate expiration date (15 days from now)
    const now = new Date();
    const expirationDate = new Date(now.getTime() + 15 * 24 * 60 * 60 * 1000); // 15 days in milliseconds
    
    // Get the partner ID from the code or product
    const partnerId = codeData.partnerId || productData.partnerId || 'default';
    console.log(`Partner ID for this code: ${partnerId}`);
    
    // Check commission rules if commission is requested
    let shouldGiveCommission = data.givePartnerCommission;
    
    // Initialize commission details to store with the code
    let commissionDetails = {
      initialCommissionPercentage: 0.10, // Default 10%
      redemptionCommissionPercentage: 0.15, // Default 15%
      allowAdminInitiatedCommission: true,
      allowPartnerInitiatedCommission: true,
      allowRedemptionCommission: true,
      initialCommissionAmount: 0 // Will be calculated below
    };
    
    if (data.givePartnerCommission && partnerId !== 'default') {
      try {
        // Get the active commission rule for this partner
        const commissionRulesCollection = collection(db, 'fragrance_commission_rules');
        const ruleQuery = query(
          commissionRulesCollection,
          where('partnerId', '==', partnerId),
          where('active', '==', true),
          limit(1)
        );
        
        const rulesSnapshot = await getDocs(ruleQuery);
        
        // Initialize commission details to store with the code
        let commissionDetails = {
          initialCommissionPercentage: 0.10, // Default 10%
          redemptionCommissionPercentage: 0.15, // Default 15%
          allowAdminInitiatedCommission: true,
          allowPartnerInitiatedCommission: true,
          allowRedemptionCommission: true,
          initialCommissionAmount: 0 // Will be calculated below
        };
        
        if (!rulesSnapshot.empty) {
          const rule = rulesSnapshot.docs[0].data() as CommissionRule;
          
          // Save the commission rule details
          commissionDetails = {
            initialCommissionPercentage: rule.initialCommissionPercentage || 0.10,
            redemptionCommissionPercentage: rule.redemptionCommissionPercentage || 0.15,
            allowAdminInitiatedCommission: rule.allowAdminInitiatedCommission !== false,
            allowPartnerInitiatedCommission: rule.allowPartnerInitiatedCommission !== false,
            allowRedemptionCommission: rule.allowRedemptionCommission !== false,
            initialCommissionAmount: 0 // Will be calculated below
          };
          
          // Check if commission is allowed based on who initiated the sale
          if (data.isAdminInitiated) {
            shouldGiveCommission = rule.allowAdminInitiatedCommission !== false;
          } else {
            shouldGiveCommission = rule.allowPartnerInitiatedCommission !== false;
          }
        }
        
        // Calculate the actual commission amount if commission is given
        if (shouldGiveCommission) {
          commissionDetails.initialCommissionAmount = price * commissionDetails.initialCommissionPercentage;
        }
        console.log(`Commission for partner: ${shouldGiveCommission ? 'Yes' : 'No'}`);
      } catch (error) {
        console.error('Error checking commission rules:', error);
        // Fall back to the requested commission setting
      }
    }
    
    // Update the code
    console.log(`Updating code ${codeId} status to sold`);
    transaction.update(codeRef, {
      isUsed: true,
      isAssigned: false,
      status: "sold",
      statusText: "Sold",
      displayStatus: "Sold",
      soldAt: Timestamp.now(),
      soldBy: data.soldBy,
      saleSource: data.saleSource,
      givePartnerCommission: shouldGiveCommission, // Use the calculated value
      customerId: data.customerId || null,
      customerName: data.customerName || null,
      customerPhone: data.customerPhone || null,
      // Save the commission details with the code for accurate returns later
      commissionDetails: commissionDetails,
      expiresAt: Timestamp.fromDate(expirationDate),
      updatedAt: serverTimestamp(),
    });
    
    console.log(`Code ${codeId} updated, returning success`);
    return { 
      success: true, 
      code: codeData.code,
      partnerId: partnerId,
      price: price,
      saleSource: data.saleSource,
      givePartnerCommission: shouldGiveCommission,
      productId: codeData.fragranceId,
      commissionAmount: commissionDetails.initialCommissionAmount
    };
  }).then(async result => {
    // Update stats after the transaction completes
    console.log(`Transaction completed, updating sales statistics`);
    try {
      if (result.partnerId) {
        await updateSalesStatistics(
          result.productId,
          result.saleSource,
          result.givePartnerCommission,
          result.price,
          'sold',
          result.partnerId
        );
        console.log(`Sales statistics updated successfully`);
        
        // Add automatic consistency check
        await ensureStatsConsistency(result.partnerId);
        
        // Update partner stock (decrease by 1) - ONLY for partner-initiated sales
        if (result.partnerId !== 'default' && !data.isAdminInitiated) {
          try {
            await updatePartnerStockOnSale(
              result.partnerId,
              result.productId,
              codeId,
              result.code
            );
            console.log(`Partner stock updated successfully for partner-initiated sale`);
          } catch (stockError) {
            console.warn('Error updating partner stock:', stockError);
            // Don't fail the operation if stock update fails
          }
        }
        
        // Update fragrance finance balance
        if (result.partnerId !== 'default' && !data.isAdminInitiated) {
          // Only record partner sale if partner initiated the sale
          await recordPartnerSale(
            result.partnerId,
            result.price,
            codeId,
            `Sale of ${result.code} - ${data.customerName || 'Customer'}`
          );
          
          // Record commission if applicable
          if (result.givePartnerCommission && result.commissionAmount > 0) {
            await recordPartnerCommission(
              result.partnerId,
              result.commissionAmount,
              codeId,
              `Initial sale commission for ${result.code}`
            );
          }
        }
      }
    } catch (error) {
      console.error('Error updating sales statistics or finance balance:', error);
      // Don't fail the operation if stats update fails
    }
    
    return { success: true, code: result.code };
  });
}

export async function revertSoldCode(codeId: string) {
  try {
    console.log(`Attempting to revert sold code ${codeId} to available status`);
    
    const codeRef = doc(codesCollection, codeId);
    let productPrice = 0; // Store product price outside transaction for later use
    let productId = ''; // Store product ID outside transaction for later use
    let partnerId = ''; // Store partner ID for consistency check
    
    // Run as a transaction to ensure data consistency
    await runTransaction(db, async (transaction) => {
      // Step 1: Read all required documents
      const codeSnap = await transaction.get(codeRef);
      
      if (!codeSnap.exists()) {
        throw new Error('Code not found');
      }
      
      const codeData = codeSnap.data() as FragranceCode;
      productId = codeData.fragranceId; // Save product ID for later use
      
      // Only allow reverting if code is sold and not redeemed
      if (codeData.status !== 'sold') {
        throw new Error('Only sold codes can be reverted');
      }
      
      if (codeData.isRedeemed) {
        throw new Error('Cannot revert redeemed codes');
      }
      
      // Get the product data to calculate revenue adjustments
      const productRef = doc(productsCollection, codeData.fragranceId);
      const productSnap = await transaction.get(productRef);
      if (!productSnap.exists()) {
        throw new Error('Product not found');
      }
      const productData = productSnap.data() as FragranceProduct;
      productPrice = productData.price; // Save product price for later use
      
      // Global sales statistics handling removed as per requirement
      
      // Get partner ID from the code
      partnerId = codeData.partnerId;
      
      // Step 2: Calculate all updates
      const price = productData.price;
      let storeRevenue = 0;
      let partnerRevenue = 0;
      
      // Get partner-specific commission percentages if partner ID exists
      let initialCommissionPercentage = 0.1; // Default 10%
      
      if (partnerId && codeData.givePartnerCommission) {
        try {
          // Import getPartnerCommissionPercentages from partnerService - can't use await import inside transaction
          // So we'll use the default value if we can't determine the exact percentage
          console.log(`Using default initial commission percentage: ${initialCommissionPercentage * 100}%`);
        } catch (error) {
          console.error('Error getting partner commission percentages:', error);
        }
      } else if (!codeData.givePartnerCommission) {
        initialCommissionPercentage = 0; // No commission if flag is false
      }
      
      // Calculate revenue adjustments based on commission settings
      if (codeData.givePartnerCommission) {
        partnerRevenue = -(price * initialCommissionPercentage); // Remove partner commission
        storeRevenue = -(price * (1 - initialCommissionPercentage)); // Remove store revenue
      } else {
        // If no commission was given
        storeRevenue = -price; // Remove full price from store revenue
      }
      
      // Step 3: Perform all writes
      // Update code status
      transaction.update(codeRef, {
        status: 'available',
        isUsed: false,
        soldAt: null,
        soldBy: null,
        customerName: null,
        customerPhone: null,
        saleSource: null,
        givePartnerCommission: null,
        partnerId: null,
        expiresAt: null,  // Clear the expiration date
        updatedAt: serverTimestamp()
      });
      
      // Global sales statistics update removed as per requirement
      
      // Update product stock
      transaction.update(productRef, {
        stock: increment(1),
        updatedAt: serverTimestamp(),
      });
    });
    
    // After transaction completes, update partner stats if partnerId exists
    const codeDoc = await getDoc(codeRef);
    if (codeDoc.exists()) {
      const codeData = codeDoc.data() as FragranceCode;
      partnerId = codeData.partnerId;
      
      if (partnerId) {
        try {
          // Import updatePartnerStats from partnerService
          const { updatePartnerStats } = await import('./partnerService');
          // Use negative values to reverse the stats
          await updatePartnerStats(
            partnerId,
            'sold', // Same action but negative values
            codeData.saleSource || 'other',
            -productPrice, // Negative price to reverse
            codeData.givePartnerCommission || false,
            productId
          );
          
          // Update partner stock (increase by 1 since item is returned) - ONLY for partner-initiated sales
          try {
            // Check if the original sale was partner-initiated by comparing saleSource with partner code
            const partnerDoc = await getDoc(doc(partnersCollection, partnerId));
            if (partnerDoc.exists()) {
              const partnerData = partnerDoc.data();
              const partnerCode = partnerData?.code;
              
              // Only update stock if the sale was originally made by this partner
              if (codeData.saleSource === partnerCode) {
                await updatePartnerStockOnReturn(
                  partnerId,
                  productId,
                  codeId,
                  codeData.code
                );
                console.log(`Partner stock updated on code revert for partner-initiated sale: ${codeData.code}`);
              } else {
                console.log(`Skipping stock update - original sale was not partner-initiated (saleSource: ${codeData.saleSource}, partnerCode: ${partnerCode})`);
              }
            }
          } catch (stockError) {
            console.warn('Error updating partner stock on revert:', stockError);
            // Don't fail the operation if stock update fails
          }
          
          // Add automatic consistency check
          await ensureStatsConsistency(partnerId);
        } catch (error) {
          console.error('Error updating partner stats:', error);
        }
      }
    }
    
    console.log(`Successfully reverted code ${codeId} to available status`);
  } catch (error) {
    console.error('Error reverting sold code:', error);
    throw error;
  }
}

export async function markCodeAsRedeemed(codeId: string) {
  const codeRef = doc(codesCollection, codeId);
  
  try {
    // Run as a transaction to ensure data consistency
    const result = await runTransaction(db, async (transaction) => {
      const codeDoc = await transaction.get(codeRef);
      
      if (!codeDoc.exists()) {
        throw new Error('Code not found');
      }
      
      const codeData = codeDoc.data() as FragranceCode;
      
      if (!codeData.isUsed) {
        throw new Error('Code has not been sold yet');
      }
      
      if (codeData.isRedeemed) {
        throw new Error('Code has already been redeemed');
      }
      
      // Check if code has expired
      if (codeData.expiresAt) {
        const now = Timestamp.now();
        if (now.toMillis() > codeData.expiresAt.toMillis()) {
          // Mark as expired but don't throw - we'll let the caller decide what to do
          transaction.update(codeRef, {
            isExpired: true,
            status: "expired",
            statusText: "Expired",
            displayStatus: "Expired",
            updatedAt: serverTimestamp()
          });
          return { success: false, expired: true, codeData: null, price: 0, partnerId: null };
        }
      }
      
      // Get the product information for price
      const productRef = doc(productsCollection, codeData.fragranceId);
      const productDoc = await transaction.get(productRef);
      
      if (!productDoc.exists()) {
        throw new Error('Product not found');
      }
      
      const productData = productDoc.data() as FragranceProduct;
      const price = productData.price || 0;
      
      // Get the partner ID from the code or product
      const partnerId = codeData.partnerId || productData.partnerId || 'default';
      const givePartnerCommission = codeData.givePartnerCommission !== undefined ? codeData.givePartnerCommission : true;
      
      // Get existing commission details from the code or create default ones
      const existingCommissionDetails = codeData.commissionDetails || {
        initialCommissionPercentage: 0.10,
        redemptionCommissionPercentage: 0.15,
        allowAdminInitiatedCommission: true,
        allowPartnerInitiatedCommission: true,
        allowRedemptionCommission: true,
        initialCommissionAmount: 0
      };
      
      // Get current commission rules for redemption
      let redemptionCommissionPercentage = existingCommissionDetails.redemptionCommissionPercentage;
      let allowRedemptionCommission = existingCommissionDetails.allowRedemptionCommission;
      
      // Try to get the latest commission rule for this partner
      if (partnerId && partnerId !== 'default') {
        try {
          // Get the active commission rule for this partner
          const commissionRulesCollection = collection(db, 'fragrance_commission_rules');
          const ruleQuery = query(
            commissionRulesCollection,
            where('partnerId', '==', partnerId),
            where('active', '==', true),
            limit(1)
          );
          
          const rulesSnapshot = await getDocs(ruleQuery);
          
          if (!rulesSnapshot.empty) {
            const rule = rulesSnapshot.docs[0].data() as CommissionRule;
            redemptionCommissionPercentage = rule.redemptionCommissionPercentage || 0.15;
            allowRedemptionCommission = rule.allowRedemptionCommission !== false;
          }
        } catch (error) {
          console.error('Error checking commission rules for redemption:', error);
          // Fall back to the existing commission details
        }
      }
      
      // Calculate the redemption commission amount
      let redemptionCommissionAmount = 0;
      if (givePartnerCommission && allowRedemptionCommission) {
        redemptionCommissionAmount = price * redemptionCommissionPercentage;
      }
      
      // Update the commission details with redemption information
      const updatedCommissionDetails = {
        ...existingCommissionDetails,
        redemptionCommissionPercentage,
        allowRedemptionCommission,
        redemptionCommissionAmount
      };
      
      // Update the code
      transaction.update(codeRef, {
        isRedeemed: true,
        status: "redeemed",
        statusText: "Redeemed",
        displayStatus: "Redeemed",
        redeemedAt: Timestamp.now(),
        commissionDetails: updatedCommissionDetails,
        updatedAt: serverTimestamp()
      });
      
      // Return the data needed for statistics update
      return { 
        success: true, 
        expired: false, 
        codeData, 
        price, 
        partnerId,
        givePartnerCommission
      };
    });
    
    // If code was marked as expired, just return the result
    if (!result.success || result.expired) {
      return result;
    }
    
    // After the transaction completes, update statistics using atomic operations
    // This ensures we don't have race conditions or partial updates
    
    // Global stats update removed as per requirement
    
    // Get commission percentages and rules for this partner
    const { 
      redemptionCommissionPercentage,
      allowRedemptionCommission
    } = result.partnerId && result.partnerId !== 'default' ? 
      await getPartnerCommissionPercentages(result.partnerId) : 
      { 
        redemptionCommissionPercentage: 0.15,
        allowRedemptionCommission: true
      };
    
    // Calculate revenue distribution for redemption (for partner stats only)
    let partnerRevenue = 0;
    
    // Check if redemption commission is allowed by the rule
    if (result.givePartnerCommission && allowRedemptionCommission) {
      // For redemptions, partner gets additional commission
      partnerRevenue = result.price * redemptionCommissionPercentage;
    } else if (!allowRedemptionCommission) {
      console.log('Redemption commission not allowed by partner rule');
    }
    
    // 2. Update partner stats if applicable
    if (result.partnerId && result.partnerId !== 'default') {
      // Import and use the partner stats update function
      const { updatePartnerStats } = await import('./partnerService');
      await updatePartnerStats(
        result.partnerId,
        'redeemed',
        result.codeData.saleSource || 'other',
        result.price,
        result.givePartnerCommission,
        result.codeData.fragranceId
      );
      
      // Update fragrance finance balance for redemption commission
      if (result.givePartnerCommission && allowRedemptionCommission && partnerRevenue > 0) {
        await recordPartnerCommission(
          result.partnerId,
          partnerRevenue,
          codeId,
          `Redemption commission for ${result.codeData.code}`
        );
      }
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error marking code as redeemed:', error);
    throw error;
  }
}

// getFragranceSalesStats function removed as per requirement - only partner stats are now used
// If you need stats, use getPartnerSalesStatsWithProducts directly

// Function to get partner-specific sales stats with product details
export async function getPartnerSalesStatsWithProducts(partnerCode: string): Promise<FragranceSalesStats & { productNames?: Record<string, string> }> {
  // Import the getPartnerStats function from partnerService
  const { getPartnerStats } = await import('./partnerService');
  
  // First, find the partner document ID using the partner code
  try {
    const partnerQuery = query(
      collection(db, 'fragrance_partners'),
      where('code', '==', partnerCode)
    );
    
    const partnerSnapshot = await getDocs(partnerQuery);
    
    if (partnerSnapshot.empty) {
      console.error(`No partner found with code: ${partnerCode}`);
      // Return empty stats if partner not found
      return {
        totalSales: 0,
        totalRedeemed: 0,
        partnerRevenue: 0,
        storeRevenue: 0,
        salesBySource: { otherSales: 0 },
        revenueBySource: { otherRevenue: 0 },
        productSales: {}
      };
    }
    
    // Get the partner document ID
    const partnerId = partnerSnapshot.docs[0].id;
    console.log(`Found partner ID: ${partnerId} for code: ${partnerCode}`);
    
    // Convert PartnerSalesStats to FragranceSalesStats format
    const partnerStats = await getPartnerStats(partnerId);
    
    if (!partnerStats) {
      // Return empty stats if partner stats not found
      return {
        totalSales: 0,
        totalRedeemed: 0,
        partnerRevenue: 0,
        storeRevenue: 0,
        salesBySource: { otherSales: 0 },
        revenueBySource: { otherRevenue: 0 },
        productSales: {}
      };
    }
    
    // Convert to FragranceSalesStats format
    const stats: FragranceSalesStats = {
      totalSales: partnerStats.totalSales || 0,
      totalRedeemed: partnerStats.totalRedeemed || 0,
      partnerRevenue: partnerStats.partnerRevenue || 0,
      storeRevenue: partnerStats.storeRevenue || 0,
      salesBySource: partnerStats.salesBySource || { otherSales: 0 },
      revenueBySource: partnerStats.revenueBySource || { otherRevenue: 0 },
      productSales: partnerStats.productSales || {}
    };
    
    // Get product names and prices for better display
    const productNames: Record<string, string> = {};
    const productPrices: Record<string, number> = {};
    
    const productIds = Object.keys(stats.productSales || {});
    
    if (productIds.length > 0) {
      try {
        // Fetch all products at once for efficiency
        const productsSnapshot = await getDocs(productsCollection);
        
        // Create maps of product IDs to names and prices
        const productNameMap = new Map<string, string>();
        const productPriceMap = new Map<string, number>();
        
        productsSnapshot.docs.forEach(doc => {
          const product = doc.data() as FragranceProduct;
          productNameMap.set(doc.id, product.name);
          productPriceMap.set(doc.id, product.price || 950); // Default price if not set
        });
        
        // Map product IDs to names and prices from our maps
        productIds.forEach(productId => {
          if (productNameMap.has(productId)) {
            productNames[productId] = productNameMap.get(productId) || 'Unknown Product';
            productPrices[productId] = productPriceMap.get(productId) || 950;
          } else {
            // For products we couldn't find, use fallback values
            productNames[productId] = `Product ${productId.substring(0, 6)}`;
            productPrices[productId] = 950; // Default price
          }
        });
      } catch (error) {
        console.error('Error fetching product data:', error);
      }
    }
    
    return {
      ...stats,
      productNames,
      productPrices
    };
  } catch (error) {
    console.error('Error finding partner:', error);
    // Return empty stats if there's an error
    return {
      totalSales: 0,
      totalRedeemed: 0,
      partnerRevenue: 0,
      storeRevenue: 0,
      salesBySource: { otherSales: 0 },
      revenueBySource: { otherRevenue: 0 },
      productSales: {}
    };
  }
}

// Export getPartnerSalesStatsWithProducts as getPartnerStats for compatibility
export const getPartnerStats = getPartnerSalesStatsWithProducts;

// Function for partners to mark a code as sold
export async function partnerMarkCodeAsSold(
  codeId: string,
  partnerCode: string,
  data: {
    customerName?: string;
    customerPhone?: string;
  }
) {
  try {
    // First, find the partner document ID using the partner code
    const partnerQuery = query(
      collection(db, 'fragrance_partners'),
      where('code', '==', partnerCode)
    );
    
    const partnerSnapshot = await getDocs(partnerQuery);
    
    if (partnerSnapshot.empty) {
      throw new Error(`No partner found with code: ${partnerCode}`);
    }
    
    // Get the partner document ID
    const partnerId = partnerSnapshot.docs[0].id;
    console.log(`Found partner ID: ${partnerId} for code: ${partnerCode}`);
    
    // Now mark the code as sold
    return markCodeAsUsed(codeId, {
      soldBy: partnerCode,
      saleSource: partnerCode, // Use partner code as the sale source
      givePartnerCommission: true, // Always give commission for partner-initiated sales
      customerName: data.customerName,
      customerPhone: data.customerPhone,
      isAdminInitiated: false // This is partner-initiated
    });
  } catch (error) {
    console.error('Error in partner marking code as sold:', error);
    throw error;
  }
}

// Function for partners to mark a code as redeemed
// Custom error types for better UI handling
export class RedemptionNotAllowedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RedemptionNotAllowedError';
  }
}

// Function to mark a code as available (used for returns)
export async function markCodeAsAvailable(codeId: string): Promise<boolean> {
  const codeRef = doc(codesCollection, codeId);
  
  try {
    let codeData: FragranceCode;
    
    // Run as a transaction to ensure data consistency
    await runTransaction(db, async (transaction) => {
      // FIRST: Perform all read operations
      const codeDoc = await transaction.get(codeRef);
      
      if (!codeDoc.exists()) {
        throw new Error('Code not found');
      }
      
      codeData = codeDoc.data() as FragranceCode;
      
      // Check if the code is in a state that can be returned
      if (!codeData.isUsed && !codeData.isRedeemed) {
        throw new Error('Code is not in a state that can be returned');
      }
      
      // Read product data if needed
      let productDoc = null;
      if (codeData.fragranceId) {
        const productRef = doc(productsCollection, codeData.fragranceId);
        productDoc = await transaction.get(productRef);
      }
      
      // SECOND: After all reads are complete, perform all write operations
      
      // Update the code status to available
      transaction.update(codeRef, {
        isUsed: false,
        isRedeemed: false,
        soldAt: null,
        redeemedAt: null,
        customerName: null,
        customerPhone: null,
        saleSource: null,
        hasReturnRequest: false,
        expiresAt: null,         // Clear the expiration date
        isExpired: false,        // Ensure the code is not marked as expired
        status: "available",
        statusText: "Available",
        displayStatus: "Available",
        updatedAt: serverTimestamp()
      });
      
      // If the code is associated with a product, update the product stock
      if (codeData.fragranceId && productDoc && productDoc.exists()) {
        const productRef = doc(productsCollection, codeData.fragranceId);
        // Increment the product stock
        transaction.update(productRef, {
          stock: increment(1),
          updatedAt: serverTimestamp()
        });
      }
    });
    
    // After transaction completes, update partner stock if applicable - ONLY for partner-initiated sales
    if (codeData.partnerId && codeData.partnerId !== 'default') {
      try {
        // Check if the original sale was partner-initiated by comparing saleSource with partner code
        const partnerDoc = await getDoc(doc(partnersCollection, codeData.partnerId));
        if (partnerDoc.exists()) {
          const partnerData = partnerDoc.data();
          const partnerCode = partnerData?.code;
          
          // Only update stock if the sale was originally made by this partner
          if (codeData.saleSource === partnerCode) {
            await updatePartnerStockOnReturn(
              codeData.partnerId,
              codeData.fragranceId,
              codeId,
              codeData.code
            );
            console.log(`Partner stock updated on return for partner-initiated sale: ${codeData.code}`);
          } else {
            console.log(`Skipping stock update - original sale was not partner-initiated (saleSource: ${codeData.saleSource}, partnerCode: ${partnerCode})`);
          }
        }
      } catch (stockError) {
        console.warn('Error updating partner stock on return:', stockError);
        // Don't fail the operation if stock update fails
      }
    }
    
    console.log(`Successfully marked code ${codeId} as available`);
    return true;
  } catch (error) {
    console.error('Error marking code as available:', error);
    throw error;
  }
}

export async function partnerMarkCodeAsRedeemed(codeId: string, partnerCode: string) {
  try {
    // First, find the partner document ID using the partner code
    const partnerQuery = query(
      collection(db, 'fragrance_partners'),
      where('code', '==', partnerCode)
    );
    
    const partnerSnapshot = await getDocs(partnerQuery);
    
    if (partnerSnapshot.empty) {
      throw new Error(`No partner found with code: ${partnerCode}`);
    }
    
    // Get the partner document ID
    const partnerId = partnerSnapshot.docs[0].id;
    console.log(`Found partner ID: ${partnerId} for code: ${partnerCode}`);
    
    // Check if redemption is allowed by the commission rule
    const { allowRedemptionCommission } = await getPartnerCommissionPercentages(partnerId);
    
    if (!allowRedemptionCommission) {
      // Throw a custom error type for better UI handling
      throw new RedemptionNotAllowedError(
        'Redemption commission is not enabled for your account. Please contact the administrator.'
      );
    }
    
    // Check if the code belongs to this partner
    const codeRef = doc(codesCollection, codeId);
    const codeDoc = await getDoc(codeRef);
    
    if (!codeDoc.exists()) {
      throw new Error('Code not found');
    }
    
    const codeData = codeDoc.data() as FragranceCode;
    
    // Verify the code belongs to this partner
    if (codeData.partnerId !== partnerId) {
      throw new Error('This code does not belong to your partner account');
    }
    
    // Now mark the code as redeemed
    return markCodeAsRedeemed(codeId);
  } catch (error) {
    console.error('Error in partner marking code as redeemed:', error);
    throw error;
  }
}

// Helper function to generate random alphanumeric string
function generateRandomString(length: number): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

// Upload fragrance image to Firebase Storage with progress tracking
export async function uploadFragranceImage(file: File, onProgress?: (progress: number) => void): Promise<string> {
  // Generate a unique filename with timestamp to avoid collisions
  const timestamp = Date.now();
  const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.]/g, '_');
  const path = `fragrances/${timestamp}_${sanitizedFileName}`;
  
  // Upload the original image with progress tracking
  return uploadImage(file, path, onProgress);
}

// Upload multiple fragrance images to Firebase Storage with progress tracking
export async function uploadMultipleFragranceImages(
  files: File[], 
  onProgress?: (progress: number, completedCount: number, totalCount: number) => void
): Promise<string[]> {
  if (files.length === 0) return [];
  
  const uploadPromises = files.map(async (file, index) => {
    const timestamp = Date.now();
    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.]/g, '_');
    const path = `fragrances/${timestamp}_${index}_${sanitizedFileName}`;
    
    // Upload each image
    const url = await uploadImage(file, path, (fileProgress) => {
      // Calculate overall progress
      const completedCount = index;
      const currentFileContribution = fileProgress / files.length;
      const completedFilesContribution = (completedCount / files.length) * 100;
      const totalProgress = completedFilesContribution + currentFileContribution;
      
      onProgress?.(Math.round(totalProgress), completedCount, files.length);
    });
    
    return url;
  });
  
  // Wait for all uploads to complete
  const urls = await Promise.all(uploadPromises);
  onProgress?.(100, files.length, files.length);
  
  return urls;
}

// Helper function to update sales statistics - now focused only on partner stats
async function updateSalesStatistics(
  fragranceId: string,
  saleSource: string, // Treat all sources as partner codes
  givePartnerCommission: boolean,
  price: number,
  action: 'sold' | 'redeemed',
  partnerId?: string | null,
  isAdminInitiated: boolean = false
): Promise<void> {
  try {
    console.log(`Updating sales statistics - Action: ${action}, Product: ${fragranceId}, Partner: ${partnerId}, Price: ${price}`);
    
    // Get the partner commission percentages
    const { initialCommissionPercentage, redemptionCommissionPercentage } = 
      partnerId ? await getPartnerCommissionPercentages(partnerId) : { initialCommissionPercentage: 0.1, redemptionCommissionPercentage: 0.15 };
    
    console.log(`Commission rates - Initial: ${initialCommissionPercentage * 100}%, Redemption: ${redemptionCommissionPercentage * 100}%`);
    
    // Calculate revenue distribution for partner stats
    let partnerRevenue = 0;
    
    if (action === 'sold' && givePartnerCommission) {
      // Initial sale commission
      partnerRevenue = price * initialCommissionPercentage;
      console.log(`Sold - Partner revenue: ${partnerRevenue}`);
    } else if (action === 'redeemed' && givePartnerCommission) {
      // IMPORTANT: For redemptions, partner gets additional commission
      partnerRevenue = price * redemptionCommissionPercentage;
      console.log(`Redeemed - Additional partner commission: ${partnerRevenue}`);
    } else if (action === 'sold') {
      // No commission case
      console.log(`Sold (no commission)`);
    }
    
    // Global stats update removed as per requirement
    console.log(`Skipping global stats update as per requirement.`);
    
    // If we have a partner, update partner stats
    if (partnerId && partnerId !== 'default') {
      // Import and use the partner stats update function
      const { updatePartnerStats } = await import('./partnerService');
      await updatePartnerStats(
        partnerId,
        action,
        saleSource,
        price,
        givePartnerCommission,
        fragranceId,
        isAdminInitiated
      );
      console.log(`Partner stats updated for ${partnerId}`);
    }
  } catch (error) {
    console.error('Error updating sales statistics:', error);
    // Don't throw - we don't want to fail the main operation if stats update fails
  }
}
